import * as React from "react";
import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "../../windowManager/browserFenster/fensterPortal";
import "./vorgangV3.css";
import { alleDokumente, alleFenster, aktivesFenster } from "../../windowManager/browserFenster/fensterRegister";

/**
 * Rechtsklick-Menü auf einem Verlaufseintrag.
 *
 * Seltene Aktionen — „Zuordnung lösen" etwa (PO 03.09.2026: „zuordnung lösen
 * kann weg. mit rechts click möglich") — belegen keine Icon-Fläche mehr,
 * bleiben aber erreichbar. Gerendert als fixed-Portal an der Mausposition, mit
 * Klemmung an den Viewport-Rand; Außenklick und Escape schließen.
 *
 * Muster wie `mail/MailContextMenu.tsx`, aber schlank und im kvp-rd-Design.
 */
export interface MenuePunkt {
    label: string;
    /** Fehlt NUR an einer Überschrift — die ist nicht klickbar. */
    onClick?: () => void;
    /** Rot einfärben (löschende Aktionen). */
    gefahr?: boolean;
    icon?: React.ReactNode;
    /** Trennlinie ÜBER diesem Punkt — gruppiert zusammengehörige Aktionen. */
    trennerDavor?: boolean;
    /**
     * Beschriftet die folgende Gruppe, statt selbst eine Aktion zu sein
     * („Anderem To-do zuweisen“ über der To-do-Liste, PO 16.09.2026). Ohne sie
     * stünden die To-do-Titel nackt zwischen den Befehlen und läsen sich wie
     * weitere Aktionen. Gerendert als `presentation`, NICHT als `menuitem`: die
     * Tastaturnavigation und jede Suche nach Menüpunkten soll sie auslassen.
     */
    ueberschrift?: boolean;
}

const RAND = 8;

export const Vg3Kontextmenue: React.FC<{
    x: number;
    y: number;
    punkte: MenuePunkt[];
    onClose: () => void;
}> = ({ x, y, punkte, onClose }) => {
    const ref = useRef<HTMLDivElement | null>(null);
    const [pos, setPos] = useState({ top: y, left: x });

    // Nach dem ersten Rendern die echte Größe messen und in den Viewport klemmen
    // — sonst läuft ein Menü am unteren/rechten Rand aus dem Bild.
    useLayoutEffect(() => {
        const el = ref.current;
        if (!el) return;
        const r = el.getBoundingClientRect();
        setPos({
            top: Math.max(RAND, Math.min(y, aktivesFenster().innerHeight - r.height - RAND)),
            left: Math.max(RAND, Math.min(x, aktivesFenster().innerWidth - r.width - RAND)),
        });
    }, [x, y]);

    useEffect(() => {
        const runter = (e: MouseEvent) => {
            if (!ref.current?.contains(e.target as Node)) onClose();
        };
        const taste = (e: KeyboardEvent) => {
            if (e.key === "Escape") onClose();
        };
        // `capture`, damit auch ein Klick in einen Portal-Nachbarn schließt.
        alleDokumente.addEventListener("pointerdown", runter, true);
        alleDokumente.addEventListener("keydown", taste);
        alleFenster.addEventListener("resize", onClose);
        alleFenster.addEventListener("scroll", onClose, true);
        return () => {
            alleDokumente.removeEventListener("pointerdown", runter, true);
            alleDokumente.removeEventListener("keydown", taste);
            alleFenster.removeEventListener("resize", onClose);
            alleFenster.removeEventListener("scroll", onClose, true);
        };
    }, [onClose]);

    if (punkte.length === 0) return null;

    return createPortal(
        <div
            ref={ref}
            className="kvp-rd vg3-kontext"
            role="menu"
            style={{ position: "fixed", top: pos.top, left: pos.left, zIndex: 3000 }}
        >
            {punkte.map((p) => (
                <React.Fragment key={p.label}>
                    {p.trennerDavor && <span className="vg3-kontext-trenner" aria-hidden />}
                    {p.ueberschrift ? (
                        <span className="vg3-kontext-ueberschrift" role="presentation">
                            {p.label}
                        </span>
                    ) : (
                        <button
                            type="button"
                            role="menuitem"
                            className={`vg3-kontext-item${p.gefahr ? " gefahr" : ""}`}
                            onClick={() => {
                                onClose();
                                p.onClick?.();
                            }}
                        >
                            {p.icon}
                            {p.label}
                        </button>
                    )}
                </React.Fragment>
            ))}
        </div>,
        document.body,
    );
};

/** Merkt sich die Mausposition eines Rechtsklicks; `null` = Menü zu. */
export function useKontextmenue() {
    const [pos, setPos] = useState<{ x: number; y: number } | null>(null);
    const oeffnen = (e: React.MouseEvent) => {
        e.preventDefault();
        e.stopPropagation();
        setPos({ x: e.clientX, y: e.clientY });
    };
    return { pos, oeffnen, schliessen: () => setPos(null) };
}
